import React, { useState } from 'react';
import { sendSuggestion } from '../../services/api';
import { FaLightbulb, FaPaperPlane } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const SuggestionPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await sendSuggestion(formData);
      if (response.success) {
        toast.success(response.message || 'Thank you for your suggestion! We value your feedback.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      toast.error(error.message || 'Failed to submit suggestion. Please try again later.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="text-center">
        <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
          Suggestions & Feedback
        </h1>
        <p className="mt-4 text-xl text-gray-500">
          We value your ideas! Share your feedback and help us improve.
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-12">
        {/* Info Column */}
        <div className="md:col-span-5 bg-gradient-to-br from-[#1E4C9A] to-[#1A8D32] text-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <FaLightbulb className="w-8 h-8 text-yellow-300 animate-pulse" />
              <h2 className="text-2xl font-bold">Your Voice Matters</h2>
            </div>
            <p className="text-gray-100 text-base leading-relaxed mb-6">
              Innovative Eco Krishi is committed to constant improvement and sustainable growth. Whether you have an idea for a new product, suggestions to enhance our services, or constructive feedback, we want to hear from you.
            </p>
            <ul className="space-y-4 text-sm text-gray-100">
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-yellow-300 rounded-full"></span>
                <span>Direct feedback to our management team</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-yellow-300 rounded-full"></span>
                <span>Help us innovate eco-friendly farming solutions</span>
              </li>
              <li className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-yellow-300 rounded-full"></span>
                <span>Every suggestion is carefully reviewed</span>
              </li>
            </ul>
          </div>

          <div className="mt-8 pt-6 border-t border-white/20 text-xs text-gray-200">
            Thank you for helping us make a greener future together!
          </div>
        </div>

        {/* Form Column */}
        <div className="md:col-span-7 bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Your Suggestion</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Suggestion / Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="5"
                placeholder="Write your suggestion or feedback here..."
                value={formData.message}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 transition-colors duration-200"
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1A8D32] text-white py-3.5 px-4 rounded-lg hover:bg-[#1E4C9A] transition-colors duration-200 font-semibold flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  <>
                    <FaPaperPlane className="w-4 h-4" />
                    <span>Submit Suggestion</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SuggestionPage;
